import { type FC, useState, useEffect, useCallback, useMemo, lazy, Suspense, type LazyExoticComponent } from 'react';

import Dropzone from '@/components/common/Dropzone';
import useConvertFile from '@/hooks/useConvertFile';
import useTranslation from '@/hooks/useTranslation';
import useDataLayer from '@/hooks/useDataLayer';
import { validateInputFile, getTargetFormats, SupportedConversionsCategories } from '@/utils/conversion';
import { getServiceErrorTranslation } from '@/utils/errors';
import { getFileExtension } from '@/utils/files';
import { isString, lazyWithPreload } from '@/utils/miscellaneous';
import { WithConfirmationReturnType } from '@/components/hoc/withConfirmation';

const TargetFormats = lazyWithPreload(() => import('@/components/conversion/TargetFormats'));
const Converting = lazyWithPreload(() => import('@/components/conversion/Converting'));
const Download = lazyWithPreload(() => import('@/components/common/Download'));
const Alert = lazy(() => import('@/components/common/Alert'));
const AdblockAlert = lazy(() => import('@/components/common/AdblockAlert'));

export interface CreateConverterProps {
  children: JSX.Element | undefined;
}

type CreateConverterOptions = {
  ConfirmationComponent?: WithConfirmationReturnType | LazyExoticComponent<WithConfirmationReturnType>;
  dropzoneHeading?: string;
  showConvertOcrButton?: boolean;
};

type ErrorCategoryType = 'info' | 'error';

export default function createConverter<P = {}>(
  WrappedComponent: FC<CreateConverterProps>,
  category: SupportedConversionsCategories,
  options: CreateConverterOptions,
  AdvertisingComponent?: FC
): FC<P & { onFinish?: () => void }> {
  const { ConfirmationComponent, dropzoneHeading, showConvertOcrButton = false } = options;

  return ({ onFinish }) => {
    const [file, setFile] = useState<File | null>(null);
    const [targetFormats, setTargetFormats] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [errorCategory, setErrorCategory] = useState<ErrorCategoryType>('info');
    const [showOcrAlert, setShowOcrAlert] = useState<boolean>(false);

    const { process, convertFile, conversionData, errorReason, resetErrorReason } = useConvertFile(onFinish);

    const { t } = useTranslation();
    const { logEvent } = useDataLayer();

    // preloading lazy components
    useEffect(() => {
      TargetFormats.preload().catch();
      Converting.preload().catch();
      Download.preload().catch();
    }, []);

    const onDrop = useCallback(
      (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) {
          return;
        }

        try {
          const inputFile = acceptedFiles[0];
          validateInputFile(inputFile, category);
          setFile(inputFile);
          logEvent('data-analytics', 'drag-and-drop-box');
        } catch (error: any) {
          setError(error.message);
        }
      },
      [logEvent]
    );

    const onReject = useCallback(() => {
      setError(t('pdfextra_convert_tool.errors.only_single_file_conversion'));
    }, [t]);

    const resetErrors = useCallback(() => {
      setError(null);
      setErrorCategory('info');
      resetErrorReason();
    }, [resetErrorReason]);

    useEffect(() => {
      (async () => {
        if (file) {
          const extension = getFileExtension(file.type);
          const targetFormats = getTargetFormats(category, extension as any);

          if (isString(targetFormats) && typeof ConfirmationComponent === 'undefined') {
            // only one target format without confirmation dialog, directly starts converting
            await convertFile(file, targetFormats);
          } else if (isString(targetFormats) && typeof ConfirmationComponent !== 'undefined') {
            // only one target format with confirmation dialog, renders dialog
            setTargetFormats([targetFormats]);
          } else if (Array.isArray(targetFormats)) {
            // multiple target formats, renders format choosing component
            setTargetFormats(targetFormats);
          }
        }
      })();
    }, [file, convertFile]);

    // handling conversion service error
    useEffect(() => {
      if (process === 'failed' && errorReason) {
        const translation = getServiceErrorTranslation(errorReason);

        setError(t(translation));
        setErrorCategory('error');
        setFile(null);
      }
    }, [process, errorReason, t]);

    const currentComponent = useMemo(() => {
      if (file === null)
        return (
          <Dropzone
            onDrop={onDrop}
            multiple={false}
            onReject={onReject}
            heading={dropzoneHeading}
            paragraphTranslation="pdfextra_convert_tool.dropzone.conversion_start_paragraph"
          />
        );

      if (process === 'pending' && targetFormats.length > 1)
        return (
          <TargetFormats
            title="pdfextra_convert_tool.target_formats.heading"
            file={file}
            formats={targetFormats as any}
            onConvert={convertFile}
            showOcrButton={showConvertOcrButton}
            onConvertOcr={() => setShowOcrAlert(true)}
          />
        );

      if (process === 'pending' && ConfirmationComponent)
        return (
          <ConfirmationComponent
            file={file}
            targetFormat={targetFormats[0]}
            onConvert={convertFile}
            showOcrButton={showConvertOcrButton}
            onConvertOcr={() => setShowOcrAlert(true)}
          />
        );

      if (process === 'uploading' || process === 'converting') return <Converting title={process} file={file} />;

      if (process === 'ready' && conversionData) return <Download file={file} conversionData={conversionData} />;
    }, [file, process, targetFormats, conversionData, onDrop, convertFile, onReject]);

    return (
      <Suspense>
        <div className="tw-widget-root">
          <WrappedComponent>
            <>
              {currentComponent}

              <Alert message={error as string} show={error !== null} category={errorCategory} onClose={resetErrors} />
              <Alert
                message={t('pdfextra_convert_tool.errors.not_supported_text_recognition')}
                show={showOcrAlert}
                category="info"
                onClose={() => setShowOcrAlert(false)}
              />
              <AdblockAlert />
            </>
          </WrappedComponent>

          {AdvertisingComponent && <AdvertisingComponent />}
        </div>
      </Suspense>
    );
  };
}
